import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';
import { EntityType, ModalService, ModalType } from '@ui';
import {
	DD_MM_YYYY,
	MigrationChecksUrl,
	MigrationExportType,
	migrationExportTypes,
	MigrationInAppRequestModel,
	MigrationTypes,
	MM_DD_YYYY,
	Notification,
	ReportActions,
	ToastService
} from '@core';
import { catchError, filter, ignoreElements, map, switchMap, take } from 'rxjs/operators';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { AppInjector } from '@app/services/app-injector.service';
import { TranslateService } from '@ngx-translate/core';
import { ReportEventConfig, ReportModalConfig } from '@app/components/report-form-config.type';
import { Report, NewReport } from '@app/core/services/reports/report.model';
import { MigrationsDataService } from '@app/services/migrations/migrations-data.service';
import { CalendarGetReportsParams } from '../pages/dashboard/models/dashboard.models';
import { Title } from '@angular/platform-browser';
import { ApiService } from '@app/core/services/api/api.service';
import { ReportsStateService } from '@app/services/reports-state/reports-state.service';
import { PassDataService } from '@app/core/services/pass-data/pass-data.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { checkUserPermissionForFinanceAndPartners } from '@app/services/helpers/helpers.helper';
import { ReportsService } from '@app/core/services/reports/reports.service';
import { ReportTypesEnum } from '@app/core/services/reports/reports.enum';
import { UserService } from '@app/core/services/user/user.service';
import { User } from '@app/core/services/user/user.model';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { Currency, ReportStatus } from '@app/core/services/settings/settings.model';
import { ErrorCode } from '@core/services/toast-service/enums';

export abstract class BaseNewReport {
	protected readonly destroyRef: DestroyRef = inject(DestroyRef);

	protected settingsService: SettingsService;
	protected modalService: ModalService;
	protected reportsService: ReportsService;
	protected translateService: TranslateService;
	protected migrationDataService: MigrationsDataService;
	protected titleService: Title;
	protected toastService: ToastService;
	protected apiService: ApiService;
	protected passDataService: PassDataService;
	protected reportsStateService: ReportsStateService;
	protected breakpointObserver: BreakpointObserver;
	protected userService: UserService;

	public currencies: Currency[];
	public defaultCurrency: Currency;
	public reportStatuses: ReportStatus[];
	public currentDateFormat: string;
	public modalsTranslator: { [key: string]: any };
	public reportsTranslator: { [key: string]: any };
	public phoneBreakPoint: boolean;

	// public autoCompletes = {
	// 	// getEmployeesForFinanceReport: null,
	// 	// getMatterByClientForFinanceReports: null,
	// 	getClientsForFinanceReport: null,
	// 	getNameAutocomplete: null,
	// 	getClientById: null,
	// 	getCasesAutocomplete: null,
	// 	getCasesAutocompleteByClient: null,
	// 	getFilterCasesAutoCompleteByClientId: null,
	// 	getMatterById: null,
	// 	getUsersAutocomplete: null,
	// 	getEmployeesAutocomplete: null,
	// 	getNameAutocompleteByMatterId: null,
	// 	getLatestCases: null,
	// 	getLatestCasesByClient: null
	// };

	public tableType: ReportTypesEnum = ReportTypesEnum.hours;

	constructor() {
		this.modalService = AppInjector.injector.get(ModalService);
		this.reportsService = AppInjector.injector.get(ReportsService);
		this.toastService = AppInjector.injector.get(ToastService);
		this.settingsService = AppInjector.injector.get(SettingsService);
		this.translateService = AppInjector.injector.get(TranslateService);
		this.titleService = AppInjector.injector.get(Title);
		this.migrationDataService = AppInjector.injector.get(MigrationsDataService);
		this.apiService = AppInjector.injector.get(ApiService);
		this.passDataService = AppInjector.injector.get(PassDataService);
		this.reportsStateService = AppInjector.injector.get(ReportsStateService);
		this.breakpointObserver = AppInjector.injector.get(BreakpointObserver);
		this.userService = AppInjector.injector.get(UserService);

		this.phoneBreakPoint = this.breakpointObserver.isMatched(Breakpoints.XSmall);

		// this.autoCompletes.getNameAutocomplete = (nameQuery: string) =>
		// 	this.apiService.getClientsForAutocomplete(nameQuery, null, AutocompleteVersion.V1);
		// this.autoCompletes.getCasesAutocomplete = (caseQuery: string) => {
		// 	const params: Params = {
		// 		active: true,
		// 		visible: true
		// 	};
		// 	return this.apiService.getMattersForAutocomplete(caseQuery, AutocompleteVersion.V3, params);
		// };
		//
		// this.autoCompletes.getClientById = (id: number) => this.apiService.getEntityById(id, 'clients');
		// this.autoCompletes.getMatterById = (id: number) => this.apiService.getEntityById(id, 'matters');
		// this.autoCompletes.getUsersAutocomplete = (query: string) =>
		// 	this.apiService
		// 		.getUsersForAutocomplete(query, AutocompleteVersion.V1, { roles: [UserRole.Finance, UserRole.Partner] })
		// 		.pipe(
		// 			map((res: any) => {
		// 				return res.map(user => {
		// 					return {
		// 						...user,
		// 						name: this.translateService.currentLang === Languages.HE ? user.alt_name : user.name
		// 					};
		// 				});
		// 			})
		// 		);
		//
		// this.autoCompletes.getEmployeesAutocomplete = (name: string) =>
		// 	this.apiService.getUsersForAutocomplete(name, AutocompleteVersion.V1).pipe(
		// 		map((res: any) => {
		// 			return res.map(user => {
		// 				return {
		// 					...user,
		// 					name: this.translateService.currentLang === Languages.HE ? user.alt_name : user.name
		// 				};
		// 			});
		// 		})
		// 	);
		//
		// this.autoCompletes.getNameAutocompleteByMatterId = (id: number) =>
		// 	this.apiService.getEntities({ matters: id }, 'clients');
		//
		// this.autoCompletes.getCasesAutocompleteByClient = (id: number) => {
		// 	const params: Params = {
		// 		limit: DEFAULT_AUTOCOMPLETE_LIMIT,
		// 		clients: id,
		// 		visible: true,
		// 		active: true,
		// 		sort: 'user_reports'
		// 	};
		// 	return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		// };
		//
		// this.autoCompletes.getLatestCases = (id?: number) => {
		// 	let params: Params = {
		// 		limit: DEFAULT_AUTOCOMPLETE_LIMIT,
		// 		sort: 'user_reports',
		// 		visible: true,
		// 		active: true
		// 	};
		// 	if (id) {
		// 		params.report_user = id;
		// 	}
		// 	return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		// };
		//
		// this.autoCompletes.getLatestCasesByClient = (id: number) => {
		// 	const params: Params = {
		// 		clients: id,
		// 		limit: DEFAULT_AUTOCOMPLETE_LIMIT,
		// 		sort: 'user_reports',
		// 		active: true,
		// 		visible: true
		// 	};
		// 	return this.apiService.getMattersForAutocomplete('', AutocompleteVersion.V3, params);
		// };
		//
		// this.autoCompletes.getFilterCasesAutoCompleteByClientId = (id: number, matterQuery: string) => {
		// 	const params: Params = {
		// 		clients: id,
		// 		limit: DEFAULT_AUTOCOMPLETE_LIMIT,
		// 		visible: true,
		// 		active: true
		// 	};
		// 	return this.apiService.getMattersForAutocomplete(matterQuery, AutocompleteVersion.V3, params);
		// };
		//
		// //TODO:SHOULD BE FOR FINANCE REPORT COMPONENT AUTOCOMPLETES IN FUTURE
		// this.autoCompletes.getClientsForFinanceReport = (name: string): Observable<Client[]> => {
		// 	return this.apiService.getClientsForAutocomplete(name, null, AutocompleteVersion.V2);
		// };

		this.getModalsTranslation();
	}

	public getModalsTranslation(): void {
		this.translateService
			.stream('MODALS')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(translation => {
				this.currentDateFormat = this.translateService.currentLang === 'he' ? DD_MM_YYYY : MM_DD_YYYY;
				this.modalsTranslator = translation;
			});
		this.translateService
			.stream('REPORTS')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(translation => {
				this.reportsTranslator = translation;
			});
	}

	//TODO: Continue to refactor this function and implement all features - close and reopen/submit and new etc..
	public openNewReportModal(config?: ReportModalConfig, originEntity?: EntityType): Observable<any> {
		let modalConfig: ReportModalConfig;
		let reportRequest: Observable<any>;
		return this.modalService.openModal(ModalType.NewReport, config).pipe(
			filter(data => !!data),
			switchMap((data: ReportEventConfig) => {
				// Fill Report data if exists
				if (data.reportData) {
					modalConfig = {
						...data,
						reportData: data.reportData
					};
				}
				// Handle cancel event
				if (data.hasOwnProperty('confirmClose')) {
					if (data.confirmClose) {
						return this.openNewReportConfirmCloseDialog(modalConfig);
					} else {
						return EMPTY.pipe(ignoreElements());
					}
				}
				reportRequest = data.reportData.id
					? this.reportsService.updateReport(data.reportData.id, data.reportData)
					: this.reportsService.createNewReport(data.reportData);
				// Create new report
				return reportRequest.pipe(
					catchError(err => {
						let error: ErrorCode = err?.error?.error ? err.error.error[0] : null;
						const errorTitle: string = error ? 'ERRORS.REPORT_PERIOD_TITLE' : null;
						if (error === ErrorCode.ReportingPeriod && config.viewAsUser) {
							error = ErrorCode.ReportingPeriodViewAs;
						}
						this.toastService.error(error, errorTitle);
						throw err;
					}),
					switchMap((res: NewReport) => {
						switch (originEntity) {
							case EntityType.BILLING_LIST:
								return of({
									...res,
									isReOpen: data.reopen
								});

							case EntityType.MATTER:
								if (data.reopen) {
									modalConfig.fillFieldsAfterSaveAndNew = { client: res.client, matter: res.matter };
									modalConfig.afterSaveAndNew = true;
									return this.openNewReportModal(modalConfig, originEntity);
								} else {
									return of(res);
								}

							case EntityType.CLIENT:
								if (data.reopen) {
									modalConfig.fillFieldsAfterSaveAndNew = { client: res.client };
									modalConfig.afterSaveAndNew = true;
									return this.openNewReportModal(modalConfig, originEntity);
								} else {
									return of(res);
								}

							default:
								if (data.reopen) {
									modalConfig.afterSaveAndNew = true;
									modalConfig.isMobileView = config.isMobileView;
									this.passDataService.changeData({ [this.reportsStateService.storeValue.activeDisplay]: true });
									return this.openNewReportModal(modalConfig);
								} else {
									return of(res);
								}
						}
					})
				);
			})
		);
	}

	public handleNewReportModal(config: ReportModalConfig): Subscription {
		return this.openNewReportModal(config)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.passDataService.changeData({ [this.reportsStateService.storeValue.activeDisplay]: true });
				this.toastService.success(this.translateService.instant('TOAST.REPORT_SUCCESS'));
			});
	}

	public getSettings(): void {
		this.settingsService.settings$.pipe(take(1)).subscribe(settings => {
			// this.descriptionTemplates = settings.description;
			// this.descriptionTemplatesTypes = settings.descriptionType;
			this.currencies = settings.currencies;
			this.reportStatuses = settings.reportStatuses;
			if (settings.firm) {
				this.defaultCurrency = settings.firm.default_currency;
				// this.firmTaxId = settings.firm.tax_id;
				// this.hoursReportCodeDefault = settings.firm.default_time_report_code;
				// this.expensesReportCodeDefault = settings.firm.default_expense_report_code;
				// this.defaultCurrencySubject$.next(true);
			}
		});
	}

	public openCreateUniformFileModal(): void {
		this.modalService
			.openModal(ModalType.CREATE_UNIFORM, null, null, this.modalsTranslator.CREATE_UNIFORM_FILE_MODAL)
			.subscribe();
	}

	public openMigrationInAppModal(notification?: Notification): void {
		const migrationType: MigrationTypes = migrationExportTypes.find((migrationExportType: MigrationExportType) => {
			return migrationExportType.id === notification?.message?.data_migration?.type;
		})?.key;
		this.modalService
			.openModal(
				ModalType.MIGRATIONS_IN_APP,
				{
					file: notification ? notification.message.link : '',
					migrationType: migrationType
				},
				null,
				this.modalsTranslator.MIGRATIONS_IN_APP
			)
			.pipe(
				filter(data => !!data?.file),
				switchMap((data: MigrationInAppRequestModel) => {
					return this.migrationDataService.checkMigrations(data.file, MigrationChecksUrl[data.type]);
				})
			)
			.subscribe((): void => {
				const { MESSAGE: message, TITLE: title }: { MESSAGE: string; TITLE: string } = this.translateService.instant(
					'NEW_NOTIFICATIONS.CHECK_MIGRATION_FILE_START'
				);
				this.toastService.customWarn(message, title);
			});
	}

	//DASHBOARD CALENDAR REPORT ACTIONS
	public openExpandedReportsFromCalendarDashboard(
		currentPath: string,
		params: CalendarGetReportsParams
	): Observable<any> {
		return this.reportsService.getReportsByDateForPartnerDashboard(currentPath, params).pipe(
			switchMap(reports => {
				return this.modalService.openModal(ModalType.SIDE_MONTHLY_STATUS, { reports }, null, this.reportsTranslator);
			})
		);
	}

	public editReportFromCalendarDashboard(event: ReportActions): Observable<any> {
		return this.reportsService.getReportById(+event.id).pipe(
			switchMap(report => {
				const config: ReportModalConfig = {
					reportData: report,
					isEditMode: true
				};
				return this.openEditOrViewReportModal(config);
			})
		);
	}

	public duplicateEntryFromSideMonthlyStatusModal(event: ReportActions): Observable<any> {
		const reportId: number = event.id;
		const currentDate: Date = new Date(event.reportObj.date);

		return this.modalService.openModal(ModalType.DUPLICATE_ENTITY, { currentDate }).pipe(
			filter(dates => !!dates),
			switchMap((dates: string[]) => {
				return this.reportsService.duplicateReportById(reportId, dates);
			}),
			filter(totalDuplicatedEntries => !!totalDuplicatedEntries),
			catchError(err => {
				let error: ErrorCode = err?.error?.error ? err.error.error[0] : null;
				const errorTitle: string = error ? 'ERRORS.REPORT_PERIOD_TITLE' : null;
				this.toastService.error(error, errorTitle);
				throw err;
			}),
			takeUntilDestroyed(this.destroyRef)
		);
	}

	public deleteReportFromCalendarDashboard(report: Report): Observable<boolean> {
		return this.modalService
			.openModal(ModalType.DEFAULT_DELETE_ITEM, report, null, this.modalsTranslator.DELETE_REPORT_MODAL)
			.pipe(
				filter(reportData => !!reportData),
				switchMap(reportData => this.reportsService.delete(reportData.id)),
				map(() => true)
			);
	}

	//New Report Modal
	private openNewReportConfirmCloseDialog(modalConfig: ReportModalConfig): Observable<any> {
		return this.modalService
			.openModal(ModalType.CONFIRM_CLOSE, null, null, this.modalsTranslator.CONFIRM_CLOSE_MODAL)
			.pipe(
				switchMap(confirmClose => {
					if (confirmClose) {
						return this.openNewReportModal({ ...modalConfig, afterCancel: true });
					} else {
						return EMPTY;
					}
				}),
				takeUntilDestroyed(this.destroyRef)
			);
	}

	public openNewReportModalWithSelectedDay(date?: string, sendInfoField?: boolean): void {
		const selectedDate: string = this.reportsStateService.storeValue.selectedDate;
		const config: ReportModalConfig = {
			reportData: {
				date: date ?? selectedDate
			},
			reportType: this.reportsStateService.activeTab,
			viewAsUser: this.reportsStateService.storeValue.viewAsUser,
			sendInfoField,
			isMobileView: this.phoneBreakPoint
		};
		this.handleNewReportModal(config);
	}

	public openEditNewReportModal(reportData: any): void {
		//TODO: Add type
		const currentUser: User = this.userService.getActiveUser();
		const isEditHoursDisabled: boolean = !(
			checkUserPermissionForFinanceAndPartners(currentUser, reportData.user.id) ||
			currentUser.id === reportData.user.id ||
			currentUser.id === reportData?.reporter
		);
		const config: ReportModalConfig = {
			reportData,
			isEditMode: true,
			reportType: reportData.time_report ? ReportTypesEnum.hours : ReportTypesEnum.expenses,
			isEditHoursDisabled,
			isMobileView: this.phoneBreakPoint
		};
		this.handleNewReportModal(config);
	}

	public openEditOrViewReportModal(config: ReportModalConfig, isMobileView?: boolean): Observable<any> {
		const modalConfig: ReportModalConfig = {
			...config,
			reportType: config.reportData.time_report ? ReportTypesEnum.hours : ReportTypesEnum.expenses,
			isMobileView
		};
		return this.openNewReportModal(modalConfig);
	}
}
